import { API_URL } from 'config.js';
import { loadLocalStorage } from './loadStorage';

export const LOGIN_STORAGE_KEY =
  process.env.NODE_ENV === 'production' ? 'segments' : 'segments-dev';

export const auth = {
  getStoredLoginInfo,
  checkAuth,
  login,
  loginWithShopifyCredentials,
  registerWithShopifyCredentials,
  register,
};

/**
 * Check Session or Local Storage for login information
 * @returns {LoginProps & {currentStore:any} | null}
 */
function getStoredLoginInfo() {
  //TODO clean older local storage keys - it can be safely removed after 2021 October 10th, once all the old tokens will already have expired
  localStorage.removeItem('Segments');
  localStorage.removeItem('Segments-Dev');
  localStorage.removeItem('Segments-preferredStore');
  localStorage.removeItem('Segments-preferredStore-Dev');

  const loginInfoString = loadLocalStorage(LOGIN_STORAGE_KEY);

  if (loginInfoString) {
    return loginInfoString;
  }
  return null;
}

function checkAuth() {
  const loginInfo = getStoredLoginInfo();
  if (loginInfo) return loginInfo;

  return {
    status: null,
    access_token: null,
    name: null,
    role_id: null,
    avatar_url: null,
    stores: null,
    currentStore: {
      completeInfo: null,
      subscriptionPlan: null,
      serializedStoreName: null,
    },
  };
}

/**
 * Process Login and set session/local storage Token
 * @param {{email: string, password: string, rememberMe: boolean}} param0
 * @returns {Promise<LoginProps>}
 */
async function login({ email, password, rememberMe }) {
  const loginInfo = await signinServer(API_URL.login, {
    email,
    password,
  });

  return loginInfo;
}

/**
 * Process Login and set session/local storage Token
 * @param {{email: string, password: string, phone?: string, name:string,referral_code?:string}} param0
 * @returns {Promise<LoginProps>}
 */
async function register({ email, password, phone, name, referral_code }) {
  const loginInfo = await signupServer(API_URL.register, {
    email,
    password,
    phone,
    name,
    referral_code,
  });

  return loginInfo;
}

/**
 * POST Request to signin to V2 API
 * @param {RequestInfo} url
 * @param {{ email: string; password: string; }} loginDetails
 * @returns {Promise<LoginProps>}
 */
async function signinServer(url, loginDetails) {
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(loginDetails),
    });
    if (!response.ok) {
      throw new Error(response.status);
    }

    /**
     * @type {LoginProps}
     */
    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
}

/**
 * POST Request to sign Up to V2 API
 * @param {RequestInfo} url
 * @param {{ email: string; password: string; name:string; phone?:string;referral_code?:string }} loginDetails
 * @returns {Promise<LoginProps>}
 */
async function signupServer(url, loginDetails) {
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(loginDetails),
    });

    if (!response.ok) {
      throw new Error(response.status);
    }

    /**
     * @type {LoginProps}
     */
    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
}

// Login and Register with Shopify Credentials Start *****

/**
 * Process Login and set session/local storage Token
 * @param {{ email: string;
 * password: string;
 * state:string,
 *  }} loginDetails
 */
async function loginWithShopifyCredentials(loginDetails) {
  const loginInfo = await signinServerWithShopifyCredentials(
    API_URL.loginWithShopifyCredentials,
    loginDetails
  );

  return loginInfo;
}

/**
 * POST Request to signin to V2 API with Shopify Credentials
 * @param {RequestInfo} url
 * @param {{ email: string;
 * password: string;
 * state:string,
 *  }} loginDetails
 * @returns {Promise<LoginProps>}
 */
async function signinServerWithShopifyCredentials(url, loginDetails) {
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(loginDetails),
    });
    if (!response.ok) {
      throw new Error(response.status);
    }

    /**
     * @type {LoginProps}
     */
    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
}

/**
 * Register User
 * @param {{ email: string;
 * password: string;
 * state:string,
 * name:string;
 * phone?:string
 * referral_code?:string
 *  }} loginDetails
 */
async function registerWithShopifyCredentials(loginDetails) {
  const loginInfo = await createUserWithShopifyCredentials(
    API_URL.registerWithShopifyCredentials,
    loginDetails
  );

  return loginInfo;
}

/**
 * POST Create User - Internal Function
 * @param {RequestInfo} url
 * @param {{ email: string;
 * password: string;
 * state:string,
 * name:string;
 * phone?:string;
 * referral_code?:string
 *  }} loginDetails
 * @returns {Promise<LoginProps>}
 */
async function createUserWithShopifyCredentials(url, loginDetails) {
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(loginDetails),
    });
    if (!response.ok) {
      throw new Error(response.status);
    }

    /**
     * @type {LoginProps}
     */
    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
}

/**
 * Login Payload Properties
 * @typedef {Object} LoginProps
 * @property {boolean?} status
 * @property {string?} access_token
 * @property {number?} id
 * @property {string?} email
 * @property {string?} name
 * @property {number?} role_id
 * @property {string?} avatar_url
 * @property {Partial<import('store/services/types').StoreInfo>[]?} stores
 */

/**
 * Active Subscription
 * @typedef {Object} ActiveSubscriptionProps
 * @property {string} level
 * @property {string} start_date
 * @property {string?} end_date
 * @property {"regular" | "trial" | "partner"} type
 */
